// src/pages/AcceptTransferPage.js
// Public — no login required. Reached via SMS link.
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getTransferByToken, respondToTransfer } from '../services/api';

function timeRemaining(expiresAt) {
  const ms = new Date(expiresAt) - Date.now();
  if (ms <= 0) return 'Expired';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m remaining` : `${m}m remaining`;
}

export default function AcceptTransferPage() {
  const { token } = useParams();
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // { status: 'accepted'|'rejected', message }
  const [error, setError] = useState('');

  useEffect(() => {
    getTransferByToken(token)
      .then(r => setTransfer(r.data))
      .catch(() => setError('Transfer not found or the link is invalid.'))
      .finally(() => setLoading(false));
  }, [token]);

  const respond = async (action) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await respondToTransfer(token, action);
      setDone({ status: res.data.status, message: res.data.message });
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Shared shell ─────────────────────────────────────────
  const Shell = ({ children }) => (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary, #0f1117)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: 'sans-serif',
    }}>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{
          width: 64, height: 64,
          background: '#e85d04',
          borderRadius: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 12px', fontSize: '1.8rem',
        }}>🏗️</div>
        <p style={{ color: '#888', fontSize: '0.85rem' }}>NGC Tracker · Next Generation Construction</p>
      </div>
      {children}
    </div>
  );

  if (loading) return (
    <Shell>
      <div style={{ color: '#ccc' }}>Loading transfer details…</div>
    </Shell>
  );

  if (error && !transfer) return (
    <Shell>
      <div style={{ color: '#ff6b6b', textAlign: 'center', maxWidth: 320 }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>❌</div>
        <h2 style={{ color: '#fff', marginBottom: 8 }}>Link Not Found</h2>
        <p>{error}</p>
      </div>
    </Shell>
  );

  if (done) return (
    <Shell>
      <div style={{ textAlign: 'center', maxWidth: 340 }}>
        <div style={{ fontSize: '4rem', marginBottom: 16 }}>
          {done.status === 'accepted' ? '✅' : '↩️'}
        </div>
        <h2 style={{ color: '#fff', marginBottom: 12 }}>
          {done.status === 'accepted' ? 'Transfer Accepted' : 'Transfer Rejected'}
        </h2>
        <p style={{ color: '#aaa', marginBottom: 28 }}>{done.message}</p>
        <a href="/" style={{
          display: 'inline-block', padding: '14px 32px', borderRadius: 10,
          background: '#e85d04', color: '#fff', fontWeight: 700,
          fontSize: '1rem', textDecoration: 'none',
        }}>Open NGC Tracker</a>
      </div>
    </Shell>
  );

  // Already resolved
  if (transfer && transfer.status !== 'pending') {
    const labels = { accepted: ['✅', 'Already Accepted', 'This transfer was already accepted.'],
                     rejected: ['↩️', 'Already Rejected', 'This transfer was already rejected.'],
                     expired:  ['⏰', 'Transfer Expired', 'This transfer link has expired.'] };
    const [icon, title, msg] = labels[transfer.status] || ['❓', 'Unknown Status', ''];
    return (
      <Shell>
        <div style={{ textAlign: 'center', maxWidth: 340 }}>
          <div style={{ fontSize: '4rem', marginBottom: 16 }}>{icon}</div>
          <h2 style={{ color: '#fff', marginBottom: 12 }}>{title}</h2>
          <p style={{ color: '#aaa', marginBottom: 28 }}>{msg}</p>
          <a href="/" style={{
            display: 'inline-block', padding: '14px 32px', borderRadius: 10,
            background: '#e85d04', color: '#fff', fontWeight: 700,
            fontSize: '1rem', textDecoration: 'none',
          }}>Open NGC Tracker</a>
        </div>
      </Shell>
    );
  }

  // Active pending transfer — show accept / reject
  return (
    <Shell>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <h2 style={{ color: '#fff', textAlign: 'center', marginBottom: 4 }}>Tool Transfer</h2>
        <p style={{ color: '#888', textAlign: 'center', fontSize: '0.85rem', marginBottom: 24 }}>
          {timeRemaining(transfer.expires_at)}
        </p>

        {/* Item card */}
        <div style={{
          background: '#1a1d27', borderRadius: 12, padding: 20, marginBottom: 20,
          border: '1px solid #2a2d3a',
        }}>
          <p style={{ color: '#888', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Tool</p>
          <h3 style={{ color: '#fff', margin: '0 0 4px' }}>{transfer.item_name}</h3>
          {transfer.asset_tag && (
            <span style={{ background: '#e85d04', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem' }}>
              {transfer.asset_tag}
            </span>
          )}
        </div>

        {/* Transfer arrow */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, padding: '0 8px' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>👤</div>
            <p style={{ color: '#888', fontSize: '0.75rem' }}>From</p>
            <p style={{ color: '#fff', fontWeight: 600 }}>{transfer.from_name}</p>
          </div>
          <div style={{ fontSize: '1.5rem', color: '#e85d04' }}>→</div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>👤</div>
            <p style={{ color: '#888', fontSize: '0.75rem' }}>To</p>
            <p style={{ color: '#fff', fontWeight: 600 }}>{transfer.to_name}</p>
            {transfer.to_location_name && (
              <p style={{ color: '#9ca3af', fontSize: '0.8rem' }}>📍 {transfer.to_location_name}</p>
            )}
          </div>
        </div>

        {error && (
          <div style={{ background: '#3a1515', border: '1px solid #ff6b6b', borderRadius: 8, padding: 12, color: '#ff6b6b', marginBottom: 16, fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <button
          disabled={submitting}
          onClick={() => respond('accept')}
          style={{
            width: '100%', padding: '16px', borderRadius: 10, border: 'none',
            background: '#22c55e', color: '#fff', fontSize: '1.1rem', fontWeight: 700,
            cursor: submitting ? 'default' : 'pointer', marginBottom: 12,
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? 'Processing…' : '✅ Accept Transfer'}
        </button>

        <button
          disabled={submitting}
          onClick={() => respond('reject')}
          style={{
            width: '100%', padding: '14px', borderRadius: 10,
            background: 'transparent', border: '1px solid #555',
            color: '#aaa', fontSize: '1rem', fontWeight: 600,
            cursor: submitting ? 'default' : 'pointer',
            opacity: submitting ? 0.7 : 1,
          }}
        >
          ↩ Reject — Keep with {transfer.from_name}
        </button>
      </div>
    </Shell>
  );
}
